import { z } from 'zod';

export const initializeListingSchema = z.object({
  section: z.number(),
  propertyType: z.number(),
  location: z.number(),
  referenceNumber: z.string().optional(),
});

export const listingDescriptionARSchema = z.object({
  'address-ar': z
    .string()
    .min(1, 'listingAddressError')
    .max(100, 'listingAddressMaxLength'),
  'title-ar': z
    .string()
    .min(1, 'listingTitleError')
    .max(100, 'listingTitleMaxLength'),
  'description-ar': z.string().min(1, 'listingDescriptionError'),
  'address-en': z
    .string()
    .optional()
    .refine((val) => !val || val.length <= 100, {
      message: 'listingAddressMaxLength',
    }),
  'title-en': z
    .string()
    .max(100, 'listingTitleMaxLength')
    .optional()
    .or(z.literal('')),
  'description-en': z.string().optional(),
});

export const listingDescriptionENSchema = z.object({
  'address-ar': z
    .string()
    .optional()
    .refine((val) => !val || val.length <= 100, {
      message: 'listingAddressMaxLength',
    }),
  'title-ar': z
    .string()
    .max(100, 'listingTitleMaxLength')
    .optional()
    .or(z.literal('')),
  'description-ar': z.string().optional(),
  'address-en': z
    .string()
    .min(1, 'listingAddressError')
    .max(100, 'listingAddressMaxLength'),
  'title-en': z
    .string()
    .min(1, 'listingTitleError')
    .max(128, 'listingTitleMaxLength'),
  'description-en': z.string().min(1, 'listingDescriptionError'),
});

export const listingDetailsSchemaForSale = z
  .object({
    area: z.number().min(1, 'areaError'),
    propertyView: z.number().min(1, 'propertyViewError'),
    attributes: z
      .array(
        z.object({
          customField: z.number(),
          value: z.union([z.string(), z.number()]),
        }),
      )
      .optional(),
    propertyRegistrationStatus: z.number().optional(),
    isMortgageApproved: z.number().optional(),
    isPropertyRegistrationStatusVisible: z.boolean().optional(),
    amenities: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.isPropertyRegistrationStatusVisible &&
      !data.propertyRegistrationStatus
    ) {
      ctx.addIssue({
        path: ['propertyRegistrationStatus'],
        code: z.ZodIssueCode.custom,
        message: 'propertyRegistrationStatusError',
      });
    }
  });

export const listingDetailsSchemaForRent = z.object({
  area: z.number().min(1, 'areaError'),
  propertyView: z.number().min(1, 'propertyViewError'),
  amenities: z.array(z.string()).optional(),
  attributes: z
    .array(
      z.object({
        customField: z.number(),
        value: z.union([z.string(), z.number()]),
      }),
    )
    .optional(),
});

export const videoSchema = z.object({
  videoUrl: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const youtubeRegex =
          /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
        return youtubeRegex.test(val);
      },
      {
        message: 'videoInvalidError',
      },
    ),
});

export const listingPriceSchemaForSale = z.object({
  paymentMethod: z.number().min(1, 'paymentMethodError'),
  price: z.number().optional(),
  paymentPlans: z
    .array(
      z
        .object({
          downPayment: z.number().optional(),
          duration: z.number().optional(),
          price: z.number().optional(),
          overPrice: z.number().optional(),
          securityDeposit: z.number().optional(),
        })
        .optional(),
    )
    .optional(),
  downPaymentType: z.enum(['percentage', 'amount']).optional(),
});

export const listingPriceSchemaForRent = z
  .object({
    price: z.number().optional(),
    paymentPlans: z.array(
      z.object({
        downPayment: z.number().optional(),
        duration: z.number(),
        price: z.number(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    data.paymentPlans.forEach((plan, index) => {
      if (plan.price === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['paymentPlans', index, 'price'],
          message: `${plan.duration}`,
        });
      }
    });
  });
